import snakeCase from 'lodash/snakeCase';

import adjustCategories from '../utils/adjust-categories';
import { API_URL } from '../utils/env';
import pluralize from '../utils/pluralize';
import storedToken from '../utils/session-data/api-token';
import makeRequest from './request';
import makeUploader from './upload';
import { withSearch } from './utils';

const request = makeRequest(API_URL, 'API_URL');
const upload = makeUploader(API_URL);

const MODELS = [
  'Account',
  'Agreement',
  'Attachment',
  'Brand',
  'BookingEntry',
  'Campaign',
  'CampaignLineCreative',
  'Contact',
  'ContactList',
  'Creative',
  'Envelope',
  'Invoice',
  'Merchant',
  'Opportunity',
  'Quote',
  'QuoteLineBundle',
  'Product',
  'Task',
  'Team',
  'User',
];

function setAuthHeaders(uploader) {
  const { xhrUpload = {} } = uploader.getState();

  const headers = {
    ...xhrUpload.headers,
    Authorization: `Bearer ${storedToken.get()}`,
  };

  uploader.setState({ xhrUpload: { ...xhrUpload, headers } });
}

function generateEndpoint(method, url) {
  return Object.assign(
    ({ id, data, search, skipError } = {}) =>
      request(
        method,
        withSearch(url.replace(/\/:id(\/|$)/, `/${id}$1`), search),
        data,
        {},
        skipError,
      ),
    { url, method },
  );
}

function generateContextEndpoint(method, url) {
  return Object.assign(
    ({ objectId, contentType, data, search }) =>
      request(
        method,
        withSearch(
          url
            .replace(/\/:type(\/|$)/, `/${contentType}$1`)
            .replace(/\/:id(\/|$)/, `/${objectId}$1`),
          search,
        ),
        data,
      ),
    { url, method },
  );
}

function generateUploadEndpoint(method, url, field, restrictions = {}) {
  const uploader = upload(method, url, field, restrictions);

  const endpoint = async meta => {
    const { meta: originalMeta } = uploader.getState();

    try {
      // Patch global and file metadata with the provided values.
      uploader.setMeta(meta);

      setAuthHeaders(uploader);

      return await uploader.upload();
    } finally {
      // Reset global metadata. We assume it is okay to leave file metadata
      // modified because file state will be discarded whereas the uploader
      // state is tied to the endpoint.
      uploader.setState({ meta: originalMeta });
    }
  };

  return Object.assign(endpoint, { url, method, uploader });
}

const API = {
  getCurrentUser: generateEndpoint('GET', '/user/me'),
  getUnreadNotificationsCount: generateEndpoint(
    'GET',
    '/user/me/comment/unread-count',
  ),
  getNotifications: generateEndpoint('GET', '/user/me/comment'),
  getUserPermissions: generateEndpoint('GET', '/user/:id/permissions'),
  recreateCampaign: generateEndpoint(
    'PATCH',
    '/campaign/:id/update_from_quote',
  ),
  getPaymentTerms: generateEndpoint('GET', '/payment_terms'),
  getPaymentTerm: generateEndpoint('GET', '/payment_terms/:id'),

  getComments: generateContextEndpoint('GET', '/:type/:id/comment'),
  addComment: generateContextEndpoint('POST', '/:type/:id/comment'),

  getCampaignLineTextBundles: generateEndpoint(
    'GET',
    '/campaign_line_text_bundles',
  ),

  getCampaignLineTextBundleById: generateEndpoint(
    'GET',
    '/campaign_line_text_bundles/:id',
  ),

  addCampaignLineTextBundles: generateEndpoint(
    'POST',
    '/campaign_line_text_bundles',
  ),

  patchCampaignLineTextBundle: generateEndpoint(
    'PATCH',
    '/campaign_line_text_bundles/:id',
  ),

  destroyTextBundle: generateEndpoint(
    'DELETE',
    '/campaign_line_text_bundles/:id',
  ),

  createSFContract: generateEndpoint('POST', 'opportunity/salesforce_contract'),

  duplicateQuote: generateEndpoint('POST', '/quote/:id/duplicate'),

  getChangelog: generateContextEndpoint('GET', '/:type/:id/changelog'),
  exportQuote: Object.assign(
    ({ id, exportParams }) =>
      request(
        'GET',
        withSearch(`/quote/${id}/export`, exportParams),
        undefined,
        { responseType: 'blob' },
      ),
    { method: 'GET', url: '/quote/:id/export' },
  ),
  downloadAttachment: Object.assign(
    ({ id }, data) =>
      request('GET', `/attachment/${id}`, data, { responseType: 'blob' }),
    { method: 'GET', url: '/attachment/:id' },
  ),

  editComment: Object.assign(
    ({ objectId, contentType, data, cId }) =>
      request('PATCH', `/${contentType}/${objectId}/comment/${cId}`, data),
    { method: 'PATCH', url: '/:type/:id/comment/:id' },
  ),
  acknowledgeComments: Object.assign(
    ({ contentType, objectId }) =>
      request('PATCH', '/user/me/comment', { contentType, objectId }),
    { method: 'PATCH', url: '/user/me/comment' },
  ),

  getCampaignsBasic: generateEndpoint('GET', '/campaign/basic'),

  getCampaignsCount: generateEndpoint('GET', '/campaign/status_count'),
  getOpportunitiesCount: generateEndpoint('GET', '/opportunity/stage_count'),
  getInvoicesCount: generateEndpoint('GET', '/invoice/status_count'),

  getCampaignBudgets: generateEndpoint('GET', '/campaign/budgets'),
  getOpportunityBudgets: generateEndpoint('GET', '/opportunity/budgets'),

  pushCampaign: Object.assign(
    ({ id, system, campaignLineIds }) =>
      request('POST', `/campaign/${id}/push_${system}`, { campaignLineIds }),
    { method: 'POST', url: '/campaign/:id/push_:system' },
  ),

  getCategories: Object.assign(
    () => request('GET', '/creative_factory_categories').then(adjustCategories),
    { method: 'GET', url: '/creative_factory_categories' },
  ),

  getCountries: Object.assign(() => request('GET', '/country'), {
    method: 'GET',
    url: '/country',
  }),

  getCommercialPackages: generateEndpoint('GET', '/commercial_package_value'),
  getCommercialPackage: generateEndpoint(
    'GET',
    '/commercial_package_value/:id',
  ),

  getDefaultProductFields: Object.assign(
    ({ countryCode, productId, gender, brandIds }) =>
      request(
        'GET',
        withSearch('/campaign_line_defaults', {
          countryCode,
          productId,
          gender,
          brandIds,
        }),
      ),
    {
      method: 'GET',
      url: '/campaign_line_defaults',
    },
  ),

  getSkus: Object.assign(
    ({ accountId, brandIds, categories, gender, country }) =>
      request(
        'GET',
        withSearch('/sku', {
          accountId,
          brandIds,
          categories,
          gender,
          country,
        }),
      ),
    { method: 'GET', url: '/sku' },
  ),

  addCampaignChange: Object.assign(
    ({ id, data }) => request('POST', `/campaign/${id}/change_request`, data),
    { method: 'POST', url: '/campaign/:id/change_request' },
  ),

  previewChangeRequestAttachment: Object.assign(
    ({ id, data }) =>
      request('POST', `/campaign/${id}/change_request/export`, data, {
        responseType: 'blob',
      }),
    { method: 'POST', url: '/campaign/:id/change_request/export' },
  ),

  patchCampaignChange: Object.assign(
    (campaignId, changeRequestId, data) =>
      request(
        'PATCH',
        `/campaign/${campaignId}/change_request/${changeRequestId}`,
        data,
      ),
    {
      method: 'PATCH',
      url: '/campaign/:id/change_request/:id',
    },
  ),

  patchCampaignChangeApproval: Object.assign(
    (campaignId, changeRequestId, approvalId, data) =>
      request(
        'PATCH',
        `/campaign/${campaignId}/change_request/${changeRequestId}/approval/${approvalId}`,
        data,
      ),
    {
      method: 'PATCH',
      url: '/campaign/:id/change_request/:id/approval/:id',
    },
  ),

  generateBookingEntries: Object.assign(
    opportunityId =>
      request('POST', `/opportunity/${opportunityId}/generate_booking_entries`),
    { method: 'POST', url: '/opportunity/:id/generate_booking_entries' },
  ),

  pushInvoicesToSAP: Object.assign(
    data => request('POST', '/invoice/push_to_sap', data),
    { method: 'POST', url: '/invoice/push_to_sap' },
  ),

  resizeCreativeImage: Object.assign(
    id => request('PATCH', `/creative/${id}/resize`, {}),
    {
      method: 'PATCH',
      url: '/creative/:id/resize',
    },
  ),

  sendCancelInvoiceEmail: Object.assign(
    (invoiceId, data) =>
      request('POST', `/invoice/${invoiceId}/send_cancel_email`, data),
    { method: 'POST', url: '/invoice/:id/send_cancel_email' },
  ),

  getAnnouncements: generateEndpoint('GET', '/announcement'),

  checkCreativeAssociations: generateContextEndpoint(
    'GET',
    '/campaign/:id/validate_creative_assignments',
  ),

  getMediaSetRequirements: generateEndpoint('GET', '/media_set_rule'),

  followAccount: generateEndpoint('POST', '/account/:id/follow'),
  unfollowAccount: generateEndpoint('POST', '/account/:id/unfollow'),

  getApprovals: opportunityId =>
    request('GET', `/opportunity/${opportunityId}/approval`),
  createApproval: (opportunityId, data) =>
    request('POST', `/opportunity/${opportunityId}/approval`, data),
  patchApproval: (opportunityId, approvalId, data) =>
    request(
      'PATCH',
      `/opportunity/${opportunityId}/approval/${approvalId}`,
      data,
    ),
  deleteApproval: (opportunityId, approvalId, data) =>
    request(
      'DELETE',
      `/opportunity/${opportunityId}/approval/${approvalId}`,
      data,
    ),

  exportAccountMasterDataTemplate: Object.assign(
    data =>
      request('POST', '/account/master_data_template', data, {
        responseType: 'blob',
      }),
    { method: 'POST', url: 'account/master_data_template' },
  ),

  exportContactsWithSegmentations: Object.assign(
    contactListId => request('POST', `/contact_list/${contactListId}/export`),
    { method: 'POST', url: '/contact_list/:id/export' },
  ),

  exportCampaigns: Object.assign(
    ({ search }) => request('POST', withSearch('/campaign/export', search)),
    {
      method: 'POST',
      url: '/campaign/export',
    },
  ),

  exportContacts: Object.assign(
    ({ search }) => request('POST', withSearch('/contact/export', search)),
    {
      method: 'POST',
      url: '/contact/export',
    },
  ),

  exportOpportunities: Object.assign(
    ({ search }) => request('POST', withSearch('/opportunity/export', search)),
    {
      method: 'POST',
      url: '/opportunity/export',
    },
  ),

  exportInvoices: Object.assign(
    ({ search }) => request('POST', withSearch('/invoice/export', search)),
    {
      method: 'POST',
      url: '/invoice/export',
    },
  ),

  exportAllInvoices: Object.assign(
    ({ search }) => request('POST', withSearch('/invoice/csv_export', search)),
    {
      method: 'POST',
      url: '/invoice/csv_export',
    },
  ),

  getKpiRecommendation: Object.assign(
    data => request('POST', '/kpi-recommendation', data),
    { method: 'POST', url: '/kpi-recommendation' },
  ),

  getPeerBrands: ({ search }) =>
    request('GET', withSearch('/peer_brands', search)),

  getCFMappings: Object.assign(
    campaignId =>
      request('GET', withSearch('/creative_factory_mapping', { campaignId })),
    { method: 'GET', url: '/creative_factory_mapping' },
  ),

  getZMSAMMerchants: generateEndpoint('GET', '/zms_am_merchant'),
  getActiveZMSAMMerchants: generateEndpoint(
    'GET',
    '/zms_am_merchant?isActive=true',
  ),
  getZMSAMMerchant: generateEndpoint('GET', '/zms_am_merchant/:id'),
  patchZMSAMMerchant: generateEndpoint('PATCH', '/zms_am_merchant/:id'),
  exportZMSAMMerchant: generateEndpoint('POST', '/zms_am_merchant/export'),

  getMerchantAttribution: generateEndpoint('GET', '/merchant_attribution'),

  getTargetSuppliers: generateEndpoint('GET', '/target_supplier'),
  getTargetSupplier: generateEndpoint('GET', '/target_supplier/:id'),

  campaignsHandover: generateEndpoint('PATCH', '/campaign/handover'),

  getOpportunitiesValidCombinations: generateEndpoint(
    'POST',
    '/opportunity/:id/valid_combinations',
  ),

  getSuggestedMerchantsAndSuppliers: generateEndpoint(
    'POST',
    '/opportunity/suggestions',
  ),
  getClmContentPreview: generateEndpoint('POST', 'campaign/clm_content'),

  addContentfulCreatives: generateEndpoint(
    'POST',
    'campaign/:id/clm_content_creatives',
  ),

  getContentfulCreatives: generateEndpoint(
    'GET',
    'campaign/:id/clm_content_creatives',
  ),

  destroyContentfulCreatives: Object.assign(
    (campaignId, id) =>
      request('DELETE', `/campaign/${campaignId}/clm_content_creatives/${id}`),
    {
      method: 'DELETE',
      url: 'campaign/:id/clm_content_creatives/:id',
    },
  ),

  getContentEntryName: Object.assign(
    data => request('POST', '/campaign/clm_content_search', data),
    { method: 'POST', url: '/campaign/clm_content_search' },
  ),
};

function createEndpoints(model, path) {
  API[`get${pluralize(model)}`] = generateEndpoint('GET', `/${path}`);
  API[`add${model}`] = generateEndpoint('POST', `/${path}`);

  API[`get${model}`] = generateEndpoint('GET', `/${path}/:id`);
  API[`patch${model}`] = generateEndpoint('PATCH', `/${path}/:id`);
  API[`destroy${model}`] = generateEndpoint('DELETE', `/${path}/:id`);
}

MODELS.forEach(model => {
  createEndpoints(model, snakeCase(model));
});

Object.assign(API, {
  addAttachment: generateUploadEndpoint('POST', '/attachment', 'file', {
    allowedFileTypes: [
      // Please refer the uppy documentation and common MIME types for different file types
      // https://uppy.io/blog/2017/07/0.17/
      // https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types
      'image/*',
      'application/pdf',
      // MS Excel
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      // MS Word
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      // MS Powerpoint
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
    maxNumberOfFiles: 5,
  }),

  addCreative: generateUploadEndpoint('POST', '/creative', 'file'),

  uploadContacts: generateUploadEndpoint('POST', '/contact/upload', 'file', {
    maxNumberOfFiles: 1,
    allowedFileTypes: ['text/csv'],
  }),
});

// You can mock a field in an API endpoint like this.
// This example adds nested field `owner` to each invoice in `/invoices`:
//
// const originalGetInvoices = API.getInvoices;
// API.getInvoices = (...args) =>
//   originalGetInvoices(...args).then(result => {
//     return result.map(resource => ({
//       ...resource,
//       owner: { id: 33, firstName: 'grace', lastName: 'hopper' },
//     }));
//   });

Object.keys(API).forEach(key => {
  API[key].apiName = key;
});

export default API;
